import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Divider,
    IconButton,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSelector } from 'react-redux';
import {addBeneficiaryAPI, getKYCAPI, updateKYCAPI} from '../../../api/CoreMlmmgmtWebServices';
import { setBankDetailsAvailable, setMlmUserObj } from 'store/slice/EcomSlice';

const BANK_VERIFICATION_LIMIT_MESSAGE = 'Bank verification attempt limit exceeded. Please connect to Our Customer Support.';

export default function BankDetails({ onBankDetailsChange }) {
    const dispatch = useDispatch();
    const [bankDetails, setBankDetails] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [beneficiaryType, setBeneficiaryType] = useState('bank');
    const [accountHolderName, setAccountHolderName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [ifscCode, setIfscCode] = useState('');
    const [upiId, setUpiId] = useState('');
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [beneficiaryResult, setBeneficiaryResult] = useState(null);
    const [showPendingWarning, setShowPendingWarning] = useState(false);
    const [kycInfo, setKycInfo] = useState(null);
    const {mlmUserObj} = useSelector((state)=>state.ecomslice.mlm);
    const { appuserInfo } = useSelector((state) => state.headerslice);
    const [openPanAadharDialog, setOpenPanAadharDialog] = useState(false);
    const [kycName, setKycName] = useState(mlmUserObj?.title || '');
    const [panNumber, setPanNumber] = useState(mlmUserObj?.pan || '');
    const [aadhaarNumber, setAadhaarNumber] = useState(mlmUserObj?.aadharnumber || '');
    const [isSavingKyc, setIsSavingKyc] = useState(false);
    const [kycApiResponse, setKycApiResponse] = useState(null);
    const [showKycResponseView, setShowKycResponseView] = useState(false);
    const isKycLocked = !!(kycApiResponse && !kycApiResponse.success
        && (kycApiResponse.message || '').includes('PAN validation attempt limit exceeded'));
    const isBankVerificationLocked =
        beneficiaryType === 'bank'
        && ((kycInfo?.accountattempts ?? 0) >= 5
            || errorMessage.includes(BANK_VERIFICATION_LIMIT_MESSAGE));

    useEffect(() => {
        fetchBankDetails();
    }, [appuserInfo?.authuserid]);
    useEffect(() => {
        
    },[mlmUserObj]);

    const fetchBankDetails = () => {
        if (!appuserInfo?.authuserid) {
            setBankDetails(null);
            dispatch(setBankDetailsAvailable(false));
            setIsFetching(false);
            return;
        }

        setIsFetching(true);
        getKYCAPI(
            {},
            (response) => {
                const rawKycInfo = response?.KycInfo || response?.kycInfo || null;
                setKycInfo(rawKycInfo);
                const resolvedRecord = normalizeKycRecord(rawKycInfo);
                if (resolvedRecord && resolvedRecord.statusid === 2) {
                    setBankDetails(resolvedRecord);
                    dispatch(setBankDetailsAvailable(true));
                    if (onBankDetailsChange) {
                        onBankDetailsChange(resolvedRecord);
                    }
                } else {
                    setBankDetails(null);
                    dispatch(setBankDetailsAvailable(false));
                }
                setIsFetching(false);
            }, (error) => {
            console.error('Error fetching KYC details:', error);
            setKycInfo(null);
            setBankDetails(null);
            dispatch(setBankDetailsAvailable(false));
            setIsFetching(false);
        });
    };

    const normalizeKycRecord = (kycRecord) => {
        if (!kycRecord) {
            return null;
        }
        const accountNumber = kycRecord.payoutaccountno || '';
        const ifscCode = kycRecord.ifsccode || '';
        const benefid = kycRecord.beneficiaryid || '';
        const statusid = kycRecord.bankaccountverified ? 2 : '';
        if (!accountNumber && !ifscCode && !benefid && !statusid) {
            return null;
        }
        return {
            type: 'bank',
            accountHolderName: kycRecord.title || '',
            accountNumber,
            ifscCode,
            upiId: '',
            addbenefreqId: null,
            benefid,
            statusid,
            title: kycRecord.title || ''
        };
    };

    const handleOpenEditDialog = () => {
        if (bankDetails) {
            setBeneficiaryType(bankDetails.type || 'bank');
            setAccountHolderName(bankDetails.accountHolderName);
            setAccountNumber(bankDetails.accountNumber || '');
            setIfscCode(bankDetails.ifscCode || '');
            setUpiId(bankDetails.upiId || '');
        } else {
            setBeneficiaryType('bank');
            setAccountHolderName('');
            setAccountNumber('');
            setIfscCode('');
            setUpiId('');
        }
        setBeneficiaryResult(null);
        setErrors({});
        setShowSuccess(false);
        setShowError(false);
        setErrorMessage('');
        setSuccessMessage('');
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
        setAccountHolderName('');
        setAccountNumber('');
        setIfscCode('');
        setUpiId('');
        setErrors({});
        setShowSuccess(false);
        setShowError(false);
        setErrorMessage('');
        setSuccessMessage('');
        setBeneficiaryResult(null);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!accountHolderName.trim()) {
            newErrors.accountHolderName = 'Name is required';
        }

        if (beneficiaryType === 'upi') {
            if (!upiId.trim()) {
                newErrors.upiId = 'UPI ID is required';
            } else if (!/^[A-Za-z0-9._-]+@[A-Za-z0-9.-]+$/.test(upiId.trim())) {
                newErrors.upiId = 'Invalid UPI ID';
            }
        } else {
            if (!accountNumber.trim()) {
                newErrors.accountNumber = 'Account number is required';
            } else if (!/^\d{10,18}$/.test(accountNumber)) {
                newErrors.accountNumber = 'Account number must be 10-18 digits';
            }

            if (!ifscCode.trim()) {
                newErrors.ifscCode = 'IFSC code is required';
            } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode.toUpperCase())) {
                newErrors.ifscCode = 'Invalid IFSC code format';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validateForm()) {
            setErrorMessage('Please fill all required fields correctly');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setShowError(false);
        setSuccessMessage('');
        setShowSuccess(false);

        const payload = {
            name: accountHolderName.trim(),
            ...(beneficiaryType === 'upi'
                ? { upiId: upiId.trim() }
                : {
                    accountNumber: accountNumber.trim(),
                    ifscCode: ifscCode.trim().toUpperCase()
                })
        };

        addBeneficiaryAPI(payload, (response) => {
            const updatedKycInfo = response?.KycInfo || response?.kycInfo || null;
            const updatedDetails = {
                type: beneficiaryType,
                accountHolderName: accountHolderName.trim(),
                accountNumber: beneficiaryType === 'bank' ? accountNumber.trim() : '',
                ifscCode: beneficiaryType === 'bank' ? ifscCode.trim().toUpperCase() : '',
                upiId: beneficiaryType === 'upi' ? upiId.trim() : '',
                addbenefreqId: null,
                benefid: updatedKycInfo?.beneficiaryid || response?.beneficiaryId || '',
                statusid: updatedKycInfo?.bankaccountverified ? 2 : ''
            };
            if (response?.success === false) {
                dispatch(setBankDetailsAvailable(false));
                setIsLoading(false);
                const failureMessage = response?.appError?.errorMsg || 'Failed to save beneficiary';
                if (failureMessage.includes(BANK_VERIFICATION_LIMIT_MESSAGE)) {
                    setKycInfo((prev) => ({ ...(prev || {}), accountattempts: 5 }));
                }
                setErrorMessage(failureMessage);
                setShowError(true);
                setShowSuccess(false);
                return;
            }
            setBeneficiaryResult({
                addbenefreqId: null,
                benefid: updatedDetails.benefid,
                statusid: updatedDetails.statusid
            });
            setIsLoading(false);
            if (updatedDetails.statusid === 2) {
                setBankDetails(updatedDetails);
                dispatch(setBankDetailsAvailable(true));
                if (onBankDetailsChange) {
                    onBankDetailsChange(updatedDetails);
                }
                setShowPendingWarning(false);
                setShowError(false);
                setSuccessMessage('Beneficiary verification completed successfully!');
                setShowSuccess(true);
            } else {
                dispatch(setBankDetailsAvailable(false));
                setShowError(false);
                setSuccessMessage('Verification is taking time, we will inform as soon as we get verification result.');
                setShowSuccess(true);
                setShowPendingWarning(true);
            }
        }, (error) => {
            dispatch(setBankDetailsAvailable(false));
            console.error('Error saving beneficiary:', error);
            const errorMsg = error;
            setErrorMessage(errorMsg || 'Failed to save beneficiary');
            setShowError(true);
            setShowSuccess(false);
            setIsLoading(false);
        });
    };

    const handleInputChange = (setter, fieldName) => (e) => {
        let value = e.target.value;
        
        if (fieldName === 'accountNumber') {
            value = value.replace(/\D/g, '');
        }
        if (fieldName === 'ifscCode') {
            value = value.toUpperCase();
        }
        
        setter(value);
        if (errors[fieldName]) {
            setErrors({ ...errors, [fieldName]: '' });
        }
        setErrorMessage('');
        setShowError(false);
    };

    const DetailRow = ({ label, value }) => (
        <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={5}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
            </Grid>
            <Grid item xs={6} sm={7}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {value || '-'}
                </Typography>
            </Grid>
        </Grid>
    );

    const SubmittedDetails = ({ details }) => {
        if (!details) {
            return null;
        }

        return (
            <Box sx={{ mt: 1 }}>
                <DetailRow label="Type" value={(details.type || 'bank').toUpperCase()} />
                {details.type === 'upi' ? (
                    <DetailRow label="UPI ID" value={details.upiId} />
                ) : (
                    <>
                        <DetailRow label="Account Number" value={details.accountNumber} />
                        <DetailRow label="IFSC Code" value={details.ifscCode} />
                    </>
                )}
                <DetailRow label="Status ID" value={details.statusid} />
            </Box>
        );
    };

    const handleOpenPanAadharDialog = () => {
        setKycName(mlmUserObj?.title || '');
        setPanNumber(mlmUserObj?.pan || '');
        setAadhaarNumber(mlmUserObj?.aadharnumber || '');
        setKycApiResponse(null);
        setShowKycResponseView(false);
        setOpenPanAadharDialog(true);
    };

    const handleClosePanAadharDialog = () => {
        setKycApiResponse(null);
        setShowKycResponseView(false);
        setOpenPanAadharDialog(false);
    };

    const handleSavePanAadhar = () => {
        setKycApiResponse(null);
        setShowKycResponseView(false);
        setIsSavingKyc(true);
        updateKYCAPI(
            {
                name: kycName.trim(),
                panNumber: panNumber.trim().toUpperCase(),
                aadharNumber: aadhaarNumber.trim()
            },
            (response) => {
                const isSuccessfulKyc = response?.success === true;
                setKycApiResponse({
                    success: isSuccessfulKyc,
                    message: response?.appError?.errorMsg || '',
                    pan: response?.pan || panNumber.trim().toUpperCase(),
                    panValid: response?.panValid,
                    panRegisteredName: response?.panRegisteredName || '',
                    panNameProvided: response?.panNameProvided || kycName.trim(),
                    panStatus: response?.panStatus || '',
                    aadhaarNumber: aadhaarNumber.trim()
                });
                setShowKycResponseView(isSuccessfulKyc);
                if (isSuccessfulKyc) {
                    dispatch(setMlmUserObj({
                        ...mlmUserObj,
                        title: kycName.trim(),
                        pan: panNumber.trim().toUpperCase(),
                        aadharnumber: aadhaarNumber.trim(),
                        panvalidated: true
                    }));
                }
                setIsSavingKyc(false);
            },
            (error) => {
                setIsSavingKyc(false);
                setKycApiResponse({
                    success: false,
                    message: typeof error === 'string' ? error : 'Failed to update KYC details',
                    pan: panNumber.trim().toUpperCase(),
                    panValid: false,
                    panRegisteredName: '',
                    panNameProvided: kycName.trim(),
                    panStatus: '',
                    aadhaarNumber: aadhaarNumber.trim()
                });
                setShowKycResponseView(false);
            }
        );
    };

    if (isFetching) {
        return (
            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 4,
                        gap: 2
                    }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            Loading bank details...
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalanceIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Bank Details
                        </Typography>
                    </Box>
                        {bankDetails && bankDetails.statusid === 2 ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                        ) : bankDetails ? (
                            <IconButton
                                onClick={handleOpenEditDialog}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'primary.50'
                                    }
                                }}
                            >
                                <EditIcon />
                            </IconButton>
                        ) : null}
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {bankDetails ? (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        Type
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {(bankDetails.type || 'bank').toUpperCase()}
                                    </Typography>
                                </Grid>
                                {bankDetails.type === 'upi' ? (
                                    <Grid item xs={6} sm={6}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            UPI ID
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {bankDetails.upiId || '-'}
                                        </Typography>
                                    </Grid>
                                ) : (
                                    <Grid item xs={6} sm={6}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            Account Number
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {bankDetails.accountNumber || '-'}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                            {bankDetails.statusid !== 2 ? (
                                <Alert icon={<WarningAmberIcon fontSize="inherit" />} severity="warning" sx={{ mt: 2 }}>
                                    Verification is in progress.
                                </Alert>
                            ) : null}
                        </Box>
                    ) : (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            py: 4,
                            gap: 2
                        }}>
                            <AccountBalanceIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Bank details not available
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleOpenEditDialog}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                            >
                                Add Bank Details
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* PAN & Aadhaar Details Box */}
            <Card sx={{ boxShadow: 3, borderRadius: 2, mt: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            PAN & Aadhaar Details
                        </Typography>
                        {mlmUserObj?.panvalidated ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                        ) : (
                            <IconButton
                                onClick={handleOpenPanAadharDialog}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'primary.50'
                                    }
                                }}
                            >
                                <EditIcon />
                            </IconButton>
                        )}
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                PAN Number
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {mlmUserObj?.pan || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Aadhaar Number
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {mlmUserObj?.aadharnumber || '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* PAN & Aadhaar Edit Dialog */}
            <Dialog
                open={openPanAadharDialog}
                onClose={handleClosePanAadharDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Edit PAN & Aadhaar Details
                        </Typography>
                        <IconButton
                            onClick={handleClosePanAadharDialog}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {showKycResponseView && kycApiResponse ? (
                        <Box>
                            <Alert severity={kycApiResponse.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                                {kycApiResponse.success
                                    ? (kycApiResponse.message || 'KYC details updated successfully')
                                    : (kycApiResponse.message || 'KYC update failed')}
                            </Alert>
                            <DetailRow label="Name" value={kycApiResponse.panNameProvided} />
                            <DetailRow label="PAN Number" value={kycApiResponse.pan} />
                            <DetailRow label="Aadhaar Number" value={kycApiResponse.aadhaarNumber} />
                        </Box>
                    ) : (
                        <Box>
                            {kycApiResponse && !kycApiResponse.success ? (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {kycApiResponse.message || 'KYC update failed'}
                                </Alert>
                            ) : null}
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        value={kycName}
                                        onChange={e => setKycName(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="PAN Number"
                                        value={panNumber}
                                        onChange={e => setPanNumber(e.target.value)}
                                        inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Aadhaar Number"
                                        value={aadhaarNumber}
                                        onChange={e => setAadhaarNumber(e.target.value)}
                                        inputProps={{ maxLength: 12 }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {showKycResponseView && kycApiResponse ? (
                        <>
                            <Button
                                onClick={handleClosePanAadharDialog}
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: 'none' }}
                            >
                                OK
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={handleClosePanAadharDialog}
                                color="inherit"
                                sx={{ textTransform: 'none' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSavePanAadhar}
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: 'none' }}
                                disabled={isSavingKyc || isKycLocked || !kycName.trim() || !panNumber.trim() || !aadhaarNumber.trim()}
                            >
                                {isSavingKyc ? 'Saving...' : (kycApiResponse && !kycApiResponse.success ? 'Resubmit' : 'Save')}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Edit/Add Bank Details Dialog */}
            <Dialog
                open={openEditDialog}
                onClose={handleCloseEditDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {bankDetails ? 'Edit Bank Details' : 'Add Bank Details'}
                        </Typography>
                        <IconButton
                            onClick={handleCloseEditDialog}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {showSuccess ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 4,
                            gap: 2
                        }}>
                            {showPendingWarning ? (
                                <WarningAmberIcon sx={{ fontSize: 64, color: 'warning.main' }} />
                            ) : (
                                <SaveIcon sx={{ fontSize: 64, color: 'success.main' }} />
                            )}
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, color: showPendingWarning ? 'warning.main' : 'success.main' }}
                            >
                                {showPendingWarning ? 'Verification Pending' : 'Success!'}
                            </Typography>
                            <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                {successMessage}
                            </Typography>
                            <Box sx={{ width: '100%', mt: 1 }}>
                                <SubmittedDetails details={bankDetails} />
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <ToggleButtonGroup
                                        value={beneficiaryType}
                                        exclusive
                                        onChange={(_, value) => value && setBeneficiaryType(value)}
                                        fullWidth
                                        color="primary"
                                        disabled={isLoading}
                                    >
                                        <ToggleButton value="bank">Bank Account</ToggleButton>
                                        <ToggleButton value="upi">UPI ID</ToggleButton>
                                    </ToggleButtonGroup>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        value={accountHolderName}
                                        onChange={handleInputChange(setAccountHolderName, 'accountHolderName')}
                                        error={!!errors.accountHolderName}
                                        helperText={errors.accountHolderName}
                                        required
                                        disabled={isLoading}
                                    />
                                </Grid>
                                {beneficiaryType === 'upi' ? (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="UPI ID"
                                            value={upiId}
                                            onChange={handleInputChange(setUpiId, 'upiId')}
                                            error={!!errors.upiId}
                                            helperText={errors.upiId}
                                            required
                                            disabled={isLoading}
                                        />
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Account Number"
                                                value={accountNumber}
                                                onChange={handleInputChange(setAccountNumber, 'accountNumber')}
                                                error={!!errors.accountNumber}
                                                helperText={errors.accountNumber}
                                                required
                                                inputProps={{ 
                                                    maxLength: 18,
                                                    inputMode: 'numeric',
                                                    pattern: '[0-9]*'
                                                }}
                                                disabled={isLoading}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="IFSC Code"
                                                value={ifscCode}
                                                onChange={handleInputChange(setIfscCode, 'ifscCode')}
                                                error={!!errors.ifscCode}
                                                helperText={errors.ifscCode}
                                                required
                                                inputProps={{ 
                                                    maxLength: 11,
                                                    style: { textTransform: 'uppercase' }
                                                }}
                                                disabled={isLoading}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            {errorMessage && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {errorMessage}
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    {showSuccess ? (
                        <Button
                            onClick={handleCloseEditDialog}
                            variant="contained"
                            color="primary"
                            sx={{ textTransform: 'none' }}
                        >
                            Close
                        </Button>
                    ) : (
                        <>
                            <Button
                                onClick={handleCloseEditDialog}
                                color="inherit"
                                sx={{ textTransform: 'none' }}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                variant="contained"
                                color="primary"
                                disabled={isLoading || isBankVerificationLocked}
                                startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                {isLoading ? 'Saving...' : 'Save'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
